import { normalizeFieldValue } from "./normalizeFieldValue";

/**
 * A ConditionGroup with its AUTHORED OPERANDS edge-normalized.
 *
 * PORT of website-builder-backend `src/lib/condition-operands.ts` (audit
 * 2026-09-23, 4c). The Worker evaluates every field-definition condition with
 * the operand trimmed (`evaluateConditionNormalized`); the runtime evaluated
 * the stored bytes. An operand typed as `"DE "` in the cockpit therefore
 * matched on the server and missed on the site — the visitor never saw a
 * field the Worker expected them to fill in.
 *
 * WHY A WRAPPER AND NOT A CHANGE TO THE EVALUATOR. `evaluate-condition.ts` is
 * under a byte-near parity contract with six twins; the operand is put into
 * the same form as the data BEFORE the evaluator sees it, and the evaluator
 * stays identical to its siblings.
 *
 * Only the `value` slot is touched (strings, and strings inside arrays for
 * `in` / `not_in`); keys, operators and structure pass through.
 *
 * CACHED BY TREE IDENTITY. A field definition's condition object is stable
 * across renders (it comes from the bootstrap), while `formInput.tsx` re-runs
 * on every keystroke; the `WeakMap` makes the rewrite happen once per tree and
 * holds no reference that would outlive the tree.
 */
const cache = new WeakMap<object, Record<string, unknown>>();

export function normalizeConditionOperands(
  node: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node)) {
    if (key === "value") {
      out[key] = Array.isArray(value)
        ? value.map((el) =>
            typeof el === "string" ? normalizeFieldValue(el) : el,
          )
        : typeof value === "string"
          ? normalizeFieldValue(value)
          : value;
    } else if (Array.isArray(value)) {
      out[key] = value.map((el) =>
        isPlainRecord(el) ? normalizeConditionOperands(el) : el,
      );
    } else if (isPlainRecord(value)) {
      out[key] = normalizeConditionOperands(value);
    } else {
      out[key] = value;
    }
  }
  return out;
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Memoized by identity: the same tree object always yields the same result.
 * `T extends object` (not `Record<string, unknown>`) so the evaluator's
 * `ConditionGroup` INTERFACE — which has no index signature — fits without a
 * cast at every call site.
 */
export function normalizedConditionTree<T extends object>(
  group: T | null | undefined,
): T | null | undefined {
  if (group === null || group === undefined) return group;
  const hit = cache.get(group);
  if (hit !== undefined) return hit as unknown as T;
  const built = normalizeConditionOperands(
    group as unknown as Record<string, unknown>,
  );
  cache.set(group, built);
  return built as unknown as T;
}
